import { Container, Graphics, Text, TextStyle, Sprite, Texture, TilingSprite } from 'pixi.js-legacy';
import { CONFIG } from '../config';
import { interpolateColor, getInvertedColor, hexStringToNumber } from '../utils/color';
import { PixiLayers } from './PixiLayers';
import { speedLineTextureCache } from './utils/SpeedLineTextureCache';
import type { Achievement } from '../data/achievements';
import type { PowerUpState } from '../types';

// Podoui 색상 팔레트
const PODO_COLORS = ['#7c4de0', '#d14a88', '#0d6a8a', '#3d8085'];

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

interface ScoreManagerLike {
    getFormattedTime(): string;
    getFormattedHighScore(): string;
}

interface AchievementManagerLike {
    getNewlyUnlocked(): Achievement[];
}

interface GameLike {
    getRenderer(): RendererLike;
    getAchievementManager(): AchievementManagerLike;
}

interface ButtonArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface GameOverButtons {
    title: ButtonArea;
    restart: ButtonArea;
}

interface PauseButtons {
    title: ButtonArea;
    continue: ButtonArea;
}

type BackgroundStyle = 'stripe' | 'diamond' | 'triangle' | 'sine' | 'hexagon' | 'grid';
type GameOverButtonSelection = 'restart' | 'title';
type PauseButtonSelection = 'continue' | 'title';

/**
 * PixiJS 기반 GameRenderer
 */
export class PixiGameRenderer {
    private game: GameLike;
    private layers: PixiLayers;

    // 캐시된 Graphics 객체들
    private backgroundSprite: Sprite | null = null;
    private backgroundTexture: Texture | null = null;
    private speedLinesTiling: TilingSprite | null = null;
    private playAreaBorderGraphics: Graphics | null = null;

    // 스피드 라인 상태 캐싱
    private lastIsLandscape: boolean = false;

    // UI 텍스트 객체들
    private scoreText: Text | null = null;
    private comboText: Text | null = null;
    private comboMultiplierText: Text | null = null;
    private comboTimerGraphics: Graphics | null = null;
    private gameOverContainer: Container | null = null;
    private pauseContainer: Container | null = null;
    private bossHealthContainer: Container | null = null;
    private powerUpContainer: Container | null = null;

    // 보스 체력바 캐시
    private cachedBossNameText: Text | null = null;
    private cachedBossHealthBg: Graphics | null = null;
    private cachedBossHealthBar: Graphics | null = null;

    // 파워업 UI 캐시 (최대 5개)
    private cachedPowerUpElements: Array<{
        icon: Graphics;
        bgBar: Graphics;
        timeBar: Graphics;
    }> = [];

    // 게임오버 UI 캐시
    private cachedGameOverOverlay: Graphics | null = null;
    private cachedGameOverText: Text | null = null;
    private cachedGameOverScoreText: Text | null = null;
    private cachedGameOverBestText: Text | null = null;
    private cachedGameOverTitleBtn: Text | null = null;
    private cachedGameOverRestartBtn: Text | null = null;
    private cachedGameOverStar: Graphics | null = null;
    private cachedAchievementTexts: Text[] = [];
    private lastAchievementIds: string[] = [];

    // 일시정지 UI 캐시
    private cachedPauseOverlay: Graphics | null = null;
    private cachedPausedText: Text | null = null;
    private cachedPauseTitleBtn: Text | null = null;
    private cachedPauseContinueBtn: Text | null = null;
    private cachedPauseStar: Graphics | null = null;

    // 상태 캐싱
    private lastHue: number = -1;
    private lastBgStyle: BackgroundStyle = 'stripe';
    private gradientCanvas: HTMLCanvasElement | null = null;
    private gradientCtx: CanvasRenderingContext2D | null = null;

    // Dirty flag 캐싱 (Graphics.clear() 및 Text 업데이트 최소화)
    private lastPlayAreaIntensity: number = -1;
    private lastComboTimerRatio: number = -1;
    private lastBossHealthRatio: number = -1;
    private lastPowerUpRatios: number[] = [];
    private lastScoreText: string = '';
    private lastCombo: number = -1;
    private lastMultiplier: number = -1;

    // FPS 표시
    private fpsText: Text | null = null;
    private fpsUpdateTimer: number = 0;
    private frameCount: number = 0;
    private currentFps: number = 0;

    constructor(game: GameLike, layers: PixiLayers) {
        this.game = game;
        this.layers = layers;
        this.initGradientCanvas();
    }

    private initGradientCanvas(): void {
        this.gradientCanvas = document.createElement('canvas');
        this.gradientCanvas.width = 1024;
        this.gradientCanvas.height = 1024;
        this.gradientCtx = this.gradientCanvas.getContext('2d', { alpha: false });
    }

    /**
     * 배경 렌더링 (그라디언트)
     */
    renderBackground(hue: number, angle: number): void {
        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();
        const canvasHeight = renderer.getCanvasHeight();

        // hue가 크게 바뀌었을 때만 텍스처 업데이트
        const hueChanged = Math.abs(this.lastHue - hue) > 1;

        if (hueChanged && this.gradientCtx && this.gradientCanvas) {
            this.lastHue = hue;

            const ctx = this.gradientCtx;
            const w = this.gradientCanvas.width;
            const h = this.gradientCanvas.height;

            const gradient = ctx.createLinearGradient(0, 0, w, h);

            const safeHue = ((hue % 360) + 360) % 360;
            const colorIndex = Math.floor(safeHue / 90) % PODO_COLORS.length;
            const nextColorIndex = (colorIndex + 1) % PODO_COLORS.length;
            const t = (safeHue % 90) / 90;

            const color1 = interpolateColor(PODO_COLORS[colorIndex], PODO_COLORS[nextColorIndex], t);
            const color2Index = (colorIndex + 2) % PODO_COLORS.length;
            const color2 = interpolateColor(PODO_COLORS[nextColorIndex], PODO_COLORS[color2Index], t);

            gradient.addColorStop(0, color1);
            gradient.addColorStop(1, color2);

            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, w, h);

            // 텍스처 업데이트 (기존 텍스처가 있으면 baseTexture.update()로 업데이트)
            if (this.backgroundTexture) {
                this.backgroundTexture.baseTexture.update();
            } else {
                this.backgroundTexture = Texture.from(this.gradientCanvas);
                // Linear 필터링으로 부드러운 스케일링 (v7: SCALE_MODES.LINEAR = 1)
                this.backgroundTexture.baseTexture.scaleMode = 1;

                this.backgroundSprite = new Sprite(this.backgroundTexture);
                this.backgroundSprite.anchor.set(0.5);
                this.layers.background.addChild(this.backgroundSprite);
            }
        }

        if (this.backgroundSprite) {
            const size = Math.max(canvasWidth, canvasHeight) * 3;
            this.backgroundSprite.position.set(canvasWidth / 2, canvasHeight / 2);
            this.backgroundSprite.width = size;
            this.backgroundSprite.height = size;
            this.backgroundSprite.rotation = angle;
        }
    }

    /**
     * 스피드라인 렌더링 (TilingSprite 기반)
     */
    renderSpeedLines(speedLineOffset: number, bgStyle: BackgroundStyle = 'stripe'): void {
        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();
        const canvasHeight = renderer.getCanvasHeight();
        const isLandscape = renderer.isLandscape;

        // 패턴 또는 방향이 변경되었는지 확인
        const styleChanged = this.lastBgStyle !== bgStyle;
        const orientationChanged = this.lastIsLandscape !== isLandscape;

        if (!this.speedLinesTiling || styleChanged || orientationChanged) {
            // 기존 TilingSprite 제거
            if (this.speedLinesTiling) {
                this.speedLinesTiling.destroy();
                this.speedLinesTiling = null;
            }

            // 새 TilingSprite 생성 (v7 API)
            const texture = speedLineTextureCache.getTexture(bgStyle, isLandscape);
            if (texture) {
                this.speedLinesTiling = new TilingSprite(texture, canvasWidth, canvasHeight);
                this.layers.speedLines.addChild(this.speedLinesTiling);
            }

            this.lastBgStyle = bgStyle;
            this.lastIsLandscape = isLandscape;
        }

        // TilingSprite 크기 업데이트 (화면 크기 변경 대응)
        if (this.speedLinesTiling) {
            this.speedLinesTiling.width = canvasWidth;
            this.speedLinesTiling.height = canvasHeight;

            // 스크롤 오프셋 적용
            if (isLandscape) {
                this.speedLinesTiling.tilePosition.x = -speedLineOffset;
                this.speedLinesTiling.tilePosition.y = 0;
            } else {
                this.speedLinesTiling.tilePosition.x = 0;
                this.speedLinesTiling.tilePosition.y = speedLineOffset;
            }
        }
    }

    /**
     * 플레이 영역 테두리 렌더링 (dirty flag - intensity 변경 시에만 redraw)
     */
    renderPlayAreaBorder(intensity: number): void {
        // intensity를 0.1 단위로 반올림하여 자주 변경되지 않도록
        const roundedIntensity = Math.round(intensity * 10) / 10;

        // intensity가 동일하면 스킵
        if (this.lastPlayAreaIntensity === roundedIntensity && this.playAreaBorderGraphics) {
            return;
        }
        this.lastPlayAreaIntensity = roundedIntensity;

        const renderer = this.game.getRenderer();
        const margin = CONFIG.PLAY_AREA.MARGIN;
        const x = margin;
        const y = margin;
        const width = renderer.getCanvasWidth() - margin * 2;
        const height = renderer.getCanvasHeight() - margin * 2;

        const opacity = 0.2 + roundedIntensity * 0.3;
        const lineWidth = 1 + roundedIntensity * 1;

        if (!this.playAreaBorderGraphics) {
            this.playAreaBorderGraphics = new Graphics();
            this.layers.playAreaBorder.addChild(this.playAreaBorderGraphics);
        }

        this.playAreaBorderGraphics.clear();
        this.playAreaBorderGraphics.lineStyle(lineWidth, 0xFFFFFF, opacity);
        this.playAreaBorderGraphics.drawRect(x, y, width, height);
    }

    /**
     * 점수 UI 렌더링
     */
    renderScoreUI(score: string): void {
        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();

        if (!this.scoreText) {
            const style = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: CONFIG.UI.SCORE_SIZE,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'center',
            });
            this.scoreText = new Text(`Score ${score}`, style);
            this.scoreText.anchor.set(0.5, 0);
            this.layers.ui.addChild(this.scoreText);
            this.lastScoreText = score;
        }

        // 값이 변경된 경우에만 텍스트 업데이트
        if (this.lastScoreText !== score) {
            this.scoreText.text = `Score ${score}`;
            this.lastScoreText = score;
        }
        this.scoreText.position.set(canvasWidth / 2, 40);
        this.scoreText.style.fill = hexStringToNumber(getInvertedColor());
    }

    /**
     * 콤보 UI 렌더링
     */
    renderComboUI(combo: number, multiplier: number, timerRatio: number): void {
        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();

        // 콤보 없으면 숨김
        if (combo === 0) {
            if (this.comboText) this.comboText.visible = false;
            if (this.comboMultiplierText) this.comboMultiplierText.visible = false;
            if (this.comboTimerGraphics) this.comboTimerGraphics.visible = false;
            return;
        }

        const comboStr = combo >= 10 ? `COMBO x${combo}` : `x${combo}`;
        const fontSize = combo >= 10 ? CONFIG.UI.SCORE_SIZE * 0.8 : CONFIG.UI.SCORE_SIZE * 0.6;
        const comboY = 40 + CONFIG.UI.SCORE_SIZE + 5;

        // 콤보 텍스트
        if (!this.comboText) {
            const style = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: fontSize,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'center',
            });
            this.comboText = new Text(comboStr, style);
            this.comboText.anchor.set(0.5, 0);
            this.comboText.alpha = 0.8;
            this.layers.ui.addChild(this.comboText);
        }

        this.comboText.visible = true;
        // 콤보가 변경된 경우에만 텍스트 업데이트
        if (this.lastCombo !== combo) {
            this.comboText.text = comboStr;
            this.comboText.style.fontSize = fontSize;
            this.lastCombo = combo;
        }
        this.comboText.position.set(canvasWidth / 2, comboY);

        let currentY = comboY + fontSize + 2;

        // 배율 텍스트
        if (!this.comboMultiplierText) {
            const style = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: CONFIG.UI.SCORE_SIZE * 0.4,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'center',
            });
            this.comboMultiplierText = new Text('', style);
            this.comboMultiplierText.anchor.set(0.5, 0);
            this.comboMultiplierText.alpha = 0.6;
            this.layers.ui.addChild(this.comboMultiplierText);
        }

        if (multiplier > 1) {
            this.comboMultiplierText.visible = true;
            // 배율이 변경된 경우에만 텍스트 업데이트
            if (this.lastMultiplier !== multiplier) {
                this.comboMultiplierText.text = `${multiplier.toFixed(1)}x`;
                this.lastMultiplier = multiplier;
            }
            this.comboMultiplierText.position.set(canvasWidth / 2, currentY);
            currentY += CONFIG.UI.SCORE_SIZE * 0.4 + 5;
        } else {
            this.comboMultiplierText.visible = false;
        }

        // 타이머 바 (dirty flag - 5% 단위 변경 시에만 redraw)
        if (!this.comboTimerGraphics) {
            this.comboTimerGraphics = new Graphics();
            this.layers.ui.addChild(this.comboTimerGraphics);
        }

        if (timerRatio > 0) {
            // 5% 단위로 반올림
            const roundedRatio = Math.round(timerRatio * 20) / 20;

            // ratio가 변경된 경우에만 redraw
            if (this.lastComboTimerRatio !== roundedRatio) {
                this.lastComboTimerRatio = roundedRatio;
                this.comboTimerGraphics.visible = true;
                this.comboTimerGraphics.clear();

                const barWidth = 60;
                const barHeight = 3;
                const barX = canvasWidth / 2 - barWidth / 2;

                this.comboTimerGraphics.beginFill(0xFFFFFF, 0.3);
                this.comboTimerGraphics.drawRect(barX, currentY, barWidth, barHeight);
                this.comboTimerGraphics.endFill();

                this.comboTimerGraphics.beginFill(0xFFFFFF, 1);
                this.comboTimerGraphics.drawRect(barX, currentY, barWidth * roundedRatio, barHeight);
                this.comboTimerGraphics.endFill();
            } else {
                this.comboTimerGraphics.visible = true;
            }
        } else {
            this.comboTimerGraphics.visible = false;
            this.lastComboTimerRatio = -1;
        }
    }

    /**
     * 보스 체력바 렌더링 (dirty flag - 2% 단위 변경 시에만 redraw)
     */
    renderBossHealthBar(health: number, maxHealth: number, bossName: string): void {
        const healthRatio = health / maxHealth;
        // 2% 단위로 반올림
        const roundedRatio = Math.round(healthRatio * 50) / 50;

        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();
        const rightMargin = 30;
        const barWidth = 120;
        const barHeight = 4;
        const barX = canvasWidth - rightMargin - barWidth;
        const nameY = 40 + CONFIG.UI.SCORE_SIZE * 0.4;
        const barY = nameY + 12;

        if (!this.bossHealthContainer) {
            this.bossHealthContainer = new Container();
            this.layers.ui.addChild(this.bossHealthContainer);
        }

        this.bossHealthContainer.visible = true;

        // 보스 이름 (캐시)
        if (!this.cachedBossNameText) {
            const nameStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: CONFIG.UI.SCORE_SIZE * 0.5,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'right',
            });
            this.cachedBossNameText = new Text(bossName, nameStyle);
            this.cachedBossNameText.anchor.set(1, 0.5);
            this.bossHealthContainer.addChild(this.cachedBossNameText);
        }
        this.cachedBossNameText.text = bossName;
        this.cachedBossNameText.position.set(canvasWidth - rightMargin, nameY);

        // 체력이 변경된 경우에만 바 redraw
        if (this.lastBossHealthRatio !== roundedRatio) {
            this.lastBossHealthRatio = roundedRatio;

            // 체력 바 배경 (캐시)
            if (!this.cachedBossHealthBg) {
                this.cachedBossHealthBg = new Graphics();
                this.bossHealthContainer.addChild(this.cachedBossHealthBg);
            }
            this.cachedBossHealthBg.clear();
            this.cachedBossHealthBg.beginFill(0xFFFFFF, 0.2);
            this.cachedBossHealthBg.drawRect(barX, barY, barWidth, barHeight);
            this.cachedBossHealthBg.endFill();

            // 체력 바 (캐시)
            if (!this.cachedBossHealthBar) {
                this.cachedBossHealthBar = new Graphics();
                this.bossHealthContainer.addChild(this.cachedBossHealthBar);
            }
            this.cachedBossHealthBar.clear();
            this.cachedBossHealthBar.beginFill(0xFFFFFF, 1);
            this.cachedBossHealthBar.drawRect(barX + barWidth * (1 - roundedRatio), barY, barWidth * roundedRatio, barHeight);
            this.cachedBossHealthBar.endFill();
        }
    }

    /**
     * 보스 체력바 숨기기
     */
    hideBossHealthBar(): void {
        if (this.bossHealthContainer) {
            this.bossHealthContainer.visible = false;
        }
    }

    /**
     * 활성 파워업 UI 렌더링 (dirty flag - 5% 단위 변경 시에만 redraw)
     */
    renderPowerUpUI(powerUps: PowerUpState[]): void {
        if (!this.powerUpContainer) {
            this.powerUpContainer = new Container();
            this.layers.ui.addChild(this.powerUpContainer);
        }

        if (powerUps.length === 0) {
            this.powerUpContainer.visible = false;
            // 모든 캐시된 요소 숨기기
            for (const elem of this.cachedPowerUpElements) {
                elem.icon.visible = false;
                elem.bgBar.visible = false;
                elem.timeBar.visible = false;
            }
            this.lastPowerUpRatios = [];
            return;
        }

        this.powerUpContainer.visible = true;

        const leftMargin = 30;
        const startY = 40 + CONFIG.UI.SCORE_SIZE * 0.4;
        const iconSize = 10;
        const barWidth = 30;
        const barHeight = 3;
        const itemSpacing = 25;

        // 필요한 캐시 요소 수 확보
        while (this.cachedPowerUpElements.length < powerUps.length) {
            const icon = new Graphics();
            const bgBar = new Graphics();
            const timeBar = new Graphics();
            this.powerUpContainer.addChild(icon);
            this.powerUpContainer.addChild(bgBar);
            this.powerUpContainer.addChild(timeBar);
            this.cachedPowerUpElements.push({ icon, bgBar, timeBar });
        }

        let y = startY;

        for (let i = 0; i < powerUps.length; i++) {
            const powerUp = powerUps[i];
            const elem = this.cachedPowerUpElements[i];
            const iconX = leftMargin + iconSize;
            const barX = iconX + iconSize + 5;
            const barY = y - barHeight / 2;
            const ratio = powerUp.remainingTime / powerUp.maxDuration;

            // 5% 단위로 반올림
            const roundedRatio = Math.round(ratio * 20) / 20;
            const lastRatio = this.lastPowerUpRatios[i] ?? -1;

            // ratio가 변경된 경우에만 redraw
            if (lastRatio !== roundedRatio) {
                this.lastPowerUpRatios[i] = roundedRatio;

                // 아이콘 업데이트 (처음 또는 타입 변경 시에만 - 여기서는 단순화)
                elem.icon.clear();
                this.drawPowerUpIconShape(elem.icon, powerUp.type, iconSize);
                elem.icon.position.set(iconX, y);

                // 배경 바
                elem.bgBar.clear();
                elem.bgBar.beginFill(0xFFFFFF, 0.2);
                elem.bgBar.drawRect(barX, barY, barWidth, barHeight);
                elem.bgBar.endFill();

                // 시간 바
                elem.timeBar.clear();
                elem.timeBar.beginFill(0xFFFFFF, 1);
                elem.timeBar.drawRect(barX, barY, barWidth * roundedRatio, barHeight);
                elem.timeBar.endFill();
            }

            elem.icon.visible = true;
            elem.bgBar.visible = true;
            elem.timeBar.visible = true;

            y += itemSpacing;
        }

        // 사용하지 않는 요소 숨기기
        for (let i = powerUps.length; i < this.cachedPowerUpElements.length; i++) {
            const elem = this.cachedPowerUpElements[i];
            elem.icon.visible = false;
            elem.bgBar.visible = false;
            elem.timeBar.visible = false;
        }
    }

    /**
     * 파워업 아이콘 모양 그리기 (Graphics 객체에 직접)
     */
    private drawPowerUpIconShape(g: Graphics, type: string, size: number): void {
        const scale = size / 10;
        g.lineStyle(1.5, 0xFFFFFF);

        if (type === 'lightning') {
            g.drawPolygon([
                2 * scale, -8 * scale,
                -2 * scale, -2 * scale,
                1 * scale, -2 * scale,
                -3 * scale, 8 * scale,
                0 * scale, 1 * scale,
                -2 * scale, 1 * scale,
                2 * scale, -8 * scale
            ]);
        } else if (type === 'clock') {
            g.drawCircle(0, 0, size * 0.8);
            g.moveTo(0, 0);
            g.lineTo(0, -size * 0.4);
            g.moveTo(0, 0);
            g.lineTo(size * 0.5, 0);
        } else if (type === 'magnet') {
            g.arc(0, 0, size * 0.6, Math.PI, 0, false);
            g.moveTo(-size * 0.6, 0);
            g.lineTo(-size * 0.6, size * 0.7);
            g.moveTo(size * 0.6, 0);
            g.lineTo(size * 0.6, size * 0.7);
        }
    }

    /**
     * 파워업 UI 숨기기
     */
    hidePowerUpUI(): void {
        if (this.powerUpContainer) {
            this.powerUpContainer.visible = false;
        }
    }

    /**
     * 게임오버 UI 렌더링 (캐시된 객체 재사용)
     */
    renderGameOverUI(
        scoreManager: ScoreManagerLike,
        buttons: GameOverButtons,
        starAngle: number = 0,
        selectedButton: GameOverButtonSelection = 'restart'
    ): void {
        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();
        const canvasHeight = renderer.getCanvasHeight();
        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2;
        const isLandscape = renderer.isLandscape;
        const yOffset = isLandscape ? -60 : 0;

        if (!this.gameOverContainer) {
            this.gameOverContainer = new Container();
            this.layers.ui.addChild(this.gameOverContainer);
        }

        this.gameOverContainer.visible = true;

        // 반투명 오버레이 (캐시)
        if (!this.cachedGameOverOverlay) {
            this.cachedGameOverOverlay = new Graphics();
            this.gameOverContainer.addChild(this.cachedGameOverOverlay);
        }
        this.cachedGameOverOverlay.clear();
        this.cachedGameOverOverlay.beginFill(0x000000, 0.3);
        this.cachedGameOverOverlay.drawRect(0, 0, canvasWidth, canvasHeight);
        this.cachedGameOverOverlay.endFill();

        // GAME OVER 텍스트 (캐시)
        if (!this.cachedGameOverText) {
            const gameOverStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: CONFIG.UI.SUBTITLE_SIZE * 1.5,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'center',
            });
            this.cachedGameOverText = new Text('GAME OVER', gameOverStyle);
            this.cachedGameOverText.anchor.set(0.5);
            this.gameOverContainer.addChild(this.cachedGameOverText);
        }
        this.cachedGameOverText.position.set(centerX, centerY - 120 + yOffset);

        // 점수 텍스트 (캐시)
        const currentScore = scoreManager.getFormattedTime();
        if (!this.cachedGameOverScoreText) {
            const scoreStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: CONFIG.UI.SUBTITLE_SIZE * 0.7,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'center',
            });
            this.cachedGameOverScoreText = new Text(`SCORE ${currentScore}`, scoreStyle);
            this.cachedGameOverScoreText.anchor.set(0.5);
            this.gameOverContainer.addChild(this.cachedGameOverScoreText);
        }
        this.cachedGameOverScoreText.text = `SCORE ${currentScore}`;
        this.cachedGameOverScoreText.position.set(centerX, centerY - 40 + yOffset);

        // 최고 점수 텍스트 (캐시)
        const highScore = scoreManager.getFormattedHighScore();
        const displayBest = parseInt(currentScore) > parseInt(highScore) ? currentScore : highScore;
        if (!this.cachedGameOverBestText) {
            const bestStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: CONFIG.UI.SUBTITLE_SIZE * 0.7,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'center',
            });
            this.cachedGameOverBestText = new Text(`BEST ${displayBest}`, bestStyle);
            this.cachedGameOverBestText.anchor.set(0.5);
            this.cachedGameOverBestText.alpha = 0.6;
            this.gameOverContainer.addChild(this.cachedGameOverBestText);
        }
        this.cachedGameOverBestText.text = `BEST ${displayBest}`;
        this.cachedGameOverBestText.position.set(centerX, centerY + yOffset);

        // 신규 업적 표시
        const achievementManager = this.game.getAchievementManager();
        const newlyUnlocked = achievementManager.getNewlyUnlocked();

        // 현재 업적 ID 목록
        const currentIds = newlyUnlocked.map(a => a.id);
        const needsUpdate = JSON.stringify(currentIds) !== JSON.stringify(this.lastAchievementIds);

        if (needsUpdate) {
            // 기존 텍스트 제거
            for (const text of this.cachedAchievementTexts) {
                text.destroy();
            }
            this.cachedAchievementTexts = [];
            this.lastAchievementIds = currentIds;

            // 새 업적 텍스트 생성
            const baseFontSize = CONFIG.UI.SCORE_SIZE;
            let contentY = centerY + 50 + yOffset;

            // 난이도 해금 체크
            const unlockedDifficulties: string[] = [];
            for (const achievement of newlyUnlocked) {
                if (achievement.id === 'death_3') {
                    unlockedDifficulties.push('Easy');
                } else if (achievement.id === 'evolve_max') {
                    unlockedDifficulties.push('Hard');
                }
            }

            // 난이도 해금 알림 (업적보다 먼저 표시)
            if (unlockedDifficulties.length > 0) {
                const unlockHeaderStyle = new TextStyle({
                    fontFamily: CONFIG.UI.FONT_FAMILY,
                    fontSize: baseFontSize * 0.7,
                    fontWeight: '100',
                    fill: 0x00FF88, // 초록색
                    align: 'center',
                });
                const unlockHeader = new Text('DIFFICULTY UNLOCKED!', unlockHeaderStyle);
                unlockHeader.anchor.set(0.5);
                unlockHeader.position.set(centerX, contentY);
                this.gameOverContainer!.addChild(unlockHeader);
                this.cachedAchievementTexts.push(unlockHeader);

                const unlockItemStyle = new TextStyle({
                    fontFamily: CONFIG.UI.FONT_FAMILY,
                    fontSize: baseFontSize * 0.6,
                    fontWeight: '100',
                    fill: 0x00FF88,
                    align: 'center',
                });
                for (let i = 0; i < unlockedDifficulties.length; i++) {
                    const unlockText = new Text(`+ ${unlockedDifficulties[i]} Mode`, unlockItemStyle);
                    unlockText.anchor.set(0.5);
                    unlockText.position.set(centerX, contentY + 30 + i * 26);
                    this.gameOverContainer!.addChild(unlockText);
                    this.cachedAchievementTexts.push(unlockText);
                }

                contentY += 30 + unlockedDifficulties.length * 26 + 16;
            }

            if (newlyUnlocked.length > 0) {
                // "NEW ACHIEVEMENTS!" 헤더 (금색)
                const headerStyle = new TextStyle({
                    fontFamily: CONFIG.UI.FONT_FAMILY,
                    fontSize: baseFontSize * 0.6,
                    fontWeight: '100',
                    fill: 0xFFD700, // 금색
                    align: 'center',
                });
                const headerText = new Text('NEW ACHIEVEMENTS!', headerStyle);
                headerText.anchor.set(0.5);
                headerText.position.set(centerX, contentY);
                this.gameOverContainer!.addChild(headerText);
                this.cachedAchievementTexts.push(headerText);

                // 각 업적 표시
                const maxDisplay = isLandscape ? 3 : 3;
                const displayCount = Math.min(newlyUnlocked.length, maxDisplay);
                const achievementStyle = new TextStyle({
                    fontFamily: CONFIG.UI.FONT_FAMILY,
                    fontSize: baseFontSize * 0.5,
                    fontWeight: '100',
                    fill: 0xFFFFFF,
                    align: 'center',
                });
                for (let i = 0; i < displayCount; i++) {
                    const achievement = newlyUnlocked[i];
                    const achText = new Text(`★ ${achievement.name}`, achievementStyle);
                    achText.anchor.set(0.5);
                    achText.position.set(centerX, contentY + 30 + i * 26);
                    this.gameOverContainer!.addChild(achText);
                    this.cachedAchievementTexts.push(achText);
                }

                // 더 있으면 "+N more" 표시
                if (newlyUnlocked.length > maxDisplay) {
                    const moreText = new Text(`+${newlyUnlocked.length - maxDisplay} more`, achievementStyle);
                    moreText.anchor.set(0.5);
                    moreText.position.set(centerX, contentY + 30 + displayCount * 26);
                    this.gameOverContainer!.addChild(moreText);
                    this.cachedAchievementTexts.push(moreText);
                }
            }
        }

        // 버튼 영역 계산
        const buttonFontSize = CONFIG.UI.SUBTITLE_SIZE * 0.7;
        const gap = 80;
        const titleWidth = 60;
        const restartWidth = 100;
        const titleX = centerX - gap / 2 - titleWidth;
        const restartX = centerX + gap / 2;
        const buttonY = canvasHeight - 50;
        const buttonHeight = 60;

        buttons.title.x = titleX - 20;
        buttons.title.y = buttonY - buttonHeight / 2;
        buttons.title.width = titleWidth + 60;
        buttons.title.height = buttonHeight;

        buttons.restart.x = restartX - 20;
        buttons.restart.y = buttonY - buttonHeight / 2;
        buttons.restart.width = restartWidth + 60;
        buttons.restart.height = buttonHeight;

        // Title 버튼 (캐시)
        if (!this.cachedGameOverTitleBtn) {
            const titleBtnStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: buttonFontSize,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'left',
            });
            this.cachedGameOverTitleBtn = new Text('TITLE', titleBtnStyle);
            this.cachedGameOverTitleBtn.anchor.set(0, 0.5);
            this.gameOverContainer.addChild(this.cachedGameOverTitleBtn);
        }
        this.cachedGameOverTitleBtn.style.fill = selectedButton === 'title' ? 0xFFFFFF : 0x888888;
        this.cachedGameOverTitleBtn.position.set(titleX, buttonY);

        // Restart 버튼 (캐시)
        if (!this.cachedGameOverRestartBtn) {
            const restartBtnStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: buttonFontSize,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'left',
            });
            this.cachedGameOverRestartBtn = new Text('RESTART', restartBtnStyle);
            this.cachedGameOverRestartBtn.anchor.set(0, 0.5);
            this.gameOverContainer.addChild(this.cachedGameOverRestartBtn);
        }
        this.cachedGameOverRestartBtn.style.fill = selectedButton === 'restart' ? 0xFFFFFF : 0x888888;
        this.cachedGameOverRestartBtn.position.set(restartX, buttonY);

        // 회전 별 (캐시)
        if (!this.cachedGameOverStar) {
            this.cachedGameOverStar = new Graphics();
            this.gameOverContainer.addChild(this.cachedGameOverStar);
        }
        this.cachedGameOverStar.clear();
        this.cachedGameOverStar.lineStyle(2, 0xFFFFFF);
        const starSize = 10;
        for (let i = 0; i < 6; i++) {
            const a = (i * Math.PI) / 3;
            this.cachedGameOverStar.moveTo(0, 0);
            this.cachedGameOverStar.lineTo(Math.cos(a) * starSize, Math.sin(a) * starSize);
        }
        this.cachedGameOverStar.rotation = starAngle;
        if (selectedButton === 'title') {
            this.cachedGameOverStar.position.set(titleX - 25, buttonY);
            this.cachedGameOverStar.visible = true;
        } else {
            this.cachedGameOverStar.position.set(restartX - 25, buttonY);
            this.cachedGameOverStar.visible = true;
        }
    }

    /**
     * 게임오버 UI 숨기기
     */
    hideGameOverUI(): void {
        if (this.gameOverContainer) {
            this.gameOverContainer.visible = false;
        }
    }

    /**
     * 일시정지 UI 렌더링 (캐시된 객체 재사용)
     */
    renderPauseUI(
        buttons: PauseButtons,
        starAngle: number = 0,
        selectedButton: PauseButtonSelection = 'continue'
    ): void {
        const renderer = this.game.getRenderer();
        const canvasWidth = renderer.getCanvasWidth();
        const canvasHeight = renderer.getCanvasHeight();
        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2;

        if (!this.pauseContainer) {
            this.pauseContainer = new Container();
            this.layers.ui.addChild(this.pauseContainer);
        }

        this.pauseContainer.visible = true;

        // 반투명 오버레이 (캐시)
        if (!this.cachedPauseOverlay) {
            this.cachedPauseOverlay = new Graphics();
            this.pauseContainer.addChild(this.cachedPauseOverlay);
        }
        this.cachedPauseOverlay.clear();
        this.cachedPauseOverlay.beginFill(0x000000, 0.5);
        this.cachedPauseOverlay.drawRect(0, 0, canvasWidth, canvasHeight);
        this.cachedPauseOverlay.endFill();

        // PAUSED 텍스트 (캐시)
        if (!this.cachedPausedText) {
            const pausedStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: CONFIG.UI.SUBTITLE_SIZE * 1.5,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'center',
            });
            this.cachedPausedText = new Text('PAUSED', pausedStyle);
            this.cachedPausedText.anchor.set(0.5);
            this.pauseContainer.addChild(this.cachedPausedText);
        }
        this.cachedPausedText.position.set(centerX, centerY - 60);

        // 버튼
        const buttonFontSize = CONFIG.UI.SUBTITLE_SIZE * 0.7;
        const gap = 60;
        const titleWidth = 60;
        const continueWidth = 100;
        const totalWidth = titleWidth + gap + continueWidth;
        const startX = centerX - totalWidth / 2;
        const titleX = startX;
        const continueX = startX + titleWidth + gap;
        const buttonY = centerY + 60;
        const buttonHeight = 60;

        buttons.title.x = titleX - 20;
        buttons.title.y = buttonY - buttonHeight / 2;
        buttons.title.width = titleWidth + 60;
        buttons.title.height = buttonHeight;

        buttons.continue.x = continueX - 20;
        buttons.continue.y = buttonY - buttonHeight / 2;
        buttons.continue.width = continueWidth + 60;
        buttons.continue.height = buttonHeight;

        // Title 버튼 (캐시)
        if (!this.cachedPauseTitleBtn) {
            const titleBtnStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: buttonFontSize,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'left',
            });
            this.cachedPauseTitleBtn = new Text('TITLE', titleBtnStyle);
            this.cachedPauseTitleBtn.anchor.set(0, 0.5);
            this.pauseContainer.addChild(this.cachedPauseTitleBtn);
        }
        this.cachedPauseTitleBtn.style.fill = selectedButton === 'title' ? 0xFFFFFF : 0x888888;
        this.cachedPauseTitleBtn.position.set(titleX, buttonY);

        // Continue 버튼 (캐시)
        if (!this.cachedPauseContinueBtn) {
            const continueBtnStyle = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: buttonFontSize,
                fontWeight: '100',
                fill: 0xFFFFFF,
                align: 'left',
            });
            this.cachedPauseContinueBtn = new Text('CONTINUE', continueBtnStyle);
            this.cachedPauseContinueBtn.anchor.set(0, 0.5);
            this.pauseContainer.addChild(this.cachedPauseContinueBtn);
        }
        this.cachedPauseContinueBtn.style.fill = selectedButton === 'continue' ? 0xFFFFFF : 0x888888;
        this.cachedPauseContinueBtn.position.set(continueX, buttonY);

        // 회전 별 (캐시)
        if (!this.cachedPauseStar) {
            this.cachedPauseStar = new Graphics();
            this.pauseContainer.addChild(this.cachedPauseStar);
        }
        this.cachedPauseStar.clear();
        this.cachedPauseStar.lineStyle(2, 0xFFFFFF);
        const starSize2 = 10;
        for (let i = 0; i < 6; i++) {
            const a = (i * Math.PI) / 3;
            this.cachedPauseStar.moveTo(0, 0);
            this.cachedPauseStar.lineTo(Math.cos(a) * starSize2, Math.sin(a) * starSize2);
        }
        this.cachedPauseStar.rotation = starAngle;
        if (selectedButton === 'title') {
            this.cachedPauseStar.position.set(titleX - 25, buttonY);
        } else {
            this.cachedPauseStar.position.set(continueX - 25, buttonY);
        }
        this.cachedPauseStar.visible = true;
    }

    /**
     * 일시정지 UI 숨기기
     */
    hidePauseUI(): void {
        if (this.pauseContainer) {
            this.pauseContainer.visible = false;
        }
    }

    /**
     * 게임 내 리스타트용 - UI 정리 (컨테이너에서 제거)
     */
    clear(): void {
        // TilingSprite는 명시적으로 destroy (중복 생성 방지)
        if (this.speedLinesTiling) {
            this.speedLinesTiling.destroy();
            this.speedLinesTiling = null;
        }
        this.lastBgStyle = 'stripe';
        this.lastIsLandscape = false;

        if (this.scoreText) {
            this.scoreText.destroy();
            this.scoreText = null;
        }
        if (this.comboText) {
            this.comboText.destroy();
            this.comboText = null;
        }
        if (this.comboMultiplierText) {
            this.comboMultiplierText.destroy();
            this.comboMultiplierText = null;
        }
        if (this.comboTimerGraphics) {
            this.comboTimerGraphics.destroy();
            this.comboTimerGraphics = null;
        }
        if (this.gameOverContainer) {
            this.gameOverContainer.destroy();
            this.gameOverContainer = null;
        }
        if (this.pauseContainer) {
            this.pauseContainer.destroy();
            this.pauseContainer = null;
        }
        if (this.bossHealthContainer) {
            this.bossHealthContainer.destroy();
            this.bossHealthContainer = null;
        }
        if (this.powerUpContainer) {
            this.powerUpContainer.destroy();
            this.powerUpContainer = null;
        }

        // 캐시된 보스 체력바 정리
        this.cachedBossNameText = null;
        this.cachedBossHealthBg = null;
        this.cachedBossHealthBar = null;

        // 캐시된 파워업 UI 정리
        this.cachedPowerUpElements = [];

        // 캐시된 게임오버/일시정지 UI 정리
        this.cachedGameOverOverlay = null;
        this.cachedGameOverText = null;
        this.cachedGameOverScoreText = null;
        this.cachedGameOverBestText = null;
        this.cachedGameOverTitleBtn = null;
        this.cachedGameOverRestartBtn = null;
        this.cachedGameOverStar = null;
        for (const text of this.cachedAchievementTexts) {
            text.destroy();
        }
        this.cachedAchievementTexts = [];
        this.lastAchievementIds = [];
        this.cachedPauseOverlay = null;
        this.cachedPausedText = null;
        this.cachedPauseTitleBtn = null;
        this.cachedPauseContinueBtn = null;
        this.cachedPauseStar = null;

        // 배경은 유지 (리스타트 시 다시 생성할 필요 없음)
    }

    /**
     * Orientation 변경 시 배경 관련 Graphics 클리어
     */
    clearBackgroundGraphics(): void {
        // TilingSprite는 orientation 변경 시 renderSpeedLines에서 자동 처리됨
        if (this.playAreaBorderGraphics) {
            this.playAreaBorderGraphics.clear();
        }
        // dirty flag 초기화 - 다음 프레임에서 강제로 다시 그리도록
        this.lastPlayAreaIntensity = -1;
    }

    /**
     * 참조 초기화 (clearAll 후 다시 생성할 수 있도록)
     */
    reset(): void {
        // TilingSprite는 명시적으로 destroy (중복 생성 방지)
        if (this.speedLinesTiling) {
            this.speedLinesTiling.destroy();
            this.speedLinesTiling = null;
        }
        // 참조만 null로 설정 (이미 removeChildren으로 컨테이너에서 제거됨)
        this.backgroundSprite = null;
        this.backgroundTexture = null;
        this.playAreaBorderGraphics = null;
        this.lastIsLandscape = false;
        this.lastBgStyle = 'stripe';
        this.scoreText = null;
        this.comboText = null;
        this.comboMultiplierText = null;
        this.comboTimerGraphics = null;
        this.gameOverContainer = null;
        this.pauseContainer = null;
        this.bossHealthContainer = null;
        this.powerUpContainer = null;
        this.lastHue = -1;

        // Dirty flag 초기화
        this.lastPlayAreaIntensity = -1;
        this.lastComboTimerRatio = -1;
        this.lastBossHealthRatio = -1;
        this.lastPowerUpRatios = [];
        this.lastScoreText = '';
        this.lastCombo = -1;
        this.lastMultiplier = -1;

        // 캐시된 보스 체력바 정리
        this.cachedBossNameText = null;
        this.cachedBossHealthBg = null;
        this.cachedBossHealthBar = null;

        // 캐시된 파워업 UI 정리
        this.cachedPowerUpElements = [];

        // 캐시된 게임오버/일시정지 UI 정리
        this.cachedGameOverOverlay = null;
        this.cachedGameOverText = null;
        this.cachedGameOverScoreText = null;
        this.cachedGameOverBestText = null;
        this.cachedGameOverTitleBtn = null;
        this.cachedGameOverRestartBtn = null;
        this.cachedGameOverStar = null;
        this.cachedAchievementTexts = [];
        this.lastAchievementIds = [];
        this.cachedPauseOverlay = null;
        this.cachedPausedText = null;
        this.cachedPauseTitleBtn = null;
        this.cachedPauseContinueBtn = null;
        this.cachedPauseStar = null;

        // FPS 텍스트 초기화
        this.fpsText = null;
        this.fpsUpdateTimer = 0;
        this.frameCount = 0;
        this.currentFps = 0;
    }

    /**
     * FPS 표시 (보스 테스트 모드용)
     */
    renderFPS(deltaTime: number): void {
        const renderer = this.game.getRenderer();
        const canvasHeight = renderer.getCanvasHeight();

        // FPS 계산
        this.frameCount++;
        this.fpsUpdateTimer += deltaTime;

        if (this.fpsUpdateTimer >= 0.5) {
            this.currentFps = Math.round(this.frameCount / this.fpsUpdateTimer);
            this.frameCount = 0;
            this.fpsUpdateTimer = 0;
        }

        // FPS 텍스트 생성/업데이트
        if (!this.fpsText) {
            const style = new TextStyle({
                fontFamily: CONFIG.UI.FONT_FAMILY,
                fontSize: 14,
                fontWeight: '400',
                fill: 0xFFFFFF,
                align: 'left',
            });
            this.fpsText = new Text(`FPS: ${this.currentFps}`, style);
            this.fpsText.anchor.set(0, 1);
            this.fpsText.alpha = 0.7;
            this.layers.ui.addChild(this.fpsText);
        }

        this.fpsText.text = `FPS: ${this.currentFps}`;
        this.fpsText.position.set(10, canvasHeight - 10);
        this.fpsText.visible = true;
    }

    /**
     * FPS 숨기기
     */
    hideFPS(): void {
        if (this.fpsText) {
            this.fpsText.visible = false;
        }
    }

    /**
     * 모든 UI 정리
     */
    cleanup(): void {
        if (this.backgroundSprite) {
            this.backgroundSprite.destroy();
            this.backgroundSprite = null;
        }
        if (this.backgroundTexture) {
            this.backgroundTexture.destroy();
            this.backgroundTexture = null;
        }
        if (this.speedLinesTiling) {
            this.speedLinesTiling.destroy();
            this.speedLinesTiling = null;
        }
        if (this.playAreaBorderGraphics) {
            this.playAreaBorderGraphics.destroy();
            this.playAreaBorderGraphics = null;
        }
        if (this.scoreText) {
            this.scoreText.destroy();
            this.scoreText = null;
        }
        if (this.comboText) {
            this.comboText.destroy();
            this.comboText = null;
        }
        if (this.comboMultiplierText) {
            this.comboMultiplierText.destroy();
            this.comboMultiplierText = null;
        }
        if (this.comboTimerGraphics) {
            this.comboTimerGraphics.destroy();
            this.comboTimerGraphics = null;
        }
        if (this.gameOverContainer) {
            this.gameOverContainer.destroy();
            this.gameOverContainer = null;
        }
        if (this.pauseContainer) {
            this.pauseContainer.destroy();
            this.pauseContainer = null;
        }
        if (this.bossHealthContainer) {
            this.bossHealthContainer.destroy();
            this.bossHealthContainer = null;
        }
        if (this.powerUpContainer) {
            this.powerUpContainer.destroy();
            this.powerUpContainer = null;
        }
        if (this.fpsText) {
            this.fpsText.destroy();
            this.fpsText = null;
        }
    }
}
